'use client';

import { useState, type ReactNode } from 'react';
import UiModal from '@/components/ui/UiModal';
import { stegaClean } from '@/lib/utils/stega';
import { ContactModule } from './ContactModule';
import type { ContactModuleContent } from './ContactModule.types';

type ContactModuleModalProps = {
    content: ContactModuleContent;
    /** Names the dialog for screen readers — the module itself shows no heading here. */
    label: string;
    /** Styles the trigger, so the caller keeps the look of its own button. */
    className?: string;
    /** The trigger's contents; falls back to the label alone. */
    children?: ReactNode;
    closeLabel?: string;
};

/**
 * The same two ways into a conversation, reachable from anywhere: a button that
 * opens them as a dialog instead of sending the reader to the contact page.
 *
 * The trigger is a button, not a link — there is no second page here, and the
 * forms post from where they stand. UiModal underneath brings the focus trap,
 * Escape, the backdrop click and the scroll lock.
 */
export function ContactModuleModal({
    content,
    label,
    className,
    children,
    closeLabel = 'Schließen',
}: ContactModuleModalProps) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                className={className}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen(true)}
            >
                {children ?? label}
            </button>

            <UiModal
                open={open}
                onClose={() => setOpen(false)}
                appear
                /* 1440px — the design's own panel, and the measure a section of
                   the same name runs at. */
                size="md"
                aria-label={stegaClean(label)}
                closeButtonLabel={closeLabel}
                panelClassName="contact-module-modal__panel"
                /* The padding is the design's, not UiModal's roomier default:
                   two columns of form need the width more than the panel needs
                   a margin. Utilities, because that is what the default is — a
                   stylesheet rule would lose to it. The scrollbar is hidden as
                   in the design; the panel scrolls under the close button. */
                bodyClassName="contact-module-modal__body scrollbar-hidden !p-[clamp(28px,3vw,40px)] !px-[clamp(28px,4vw,48px)]"
            >
                {/* Compact: the intro belongs to the section on the page — in here
                    the two column heads are the headings. */}
                <ContactModule content={content} compact />
            </UiModal>
        </>
    );
}
